import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAisReleasedDeclarationNotification } from 'store/declarations/client';
import { getAllIrelandImportDeclarationProducts } from 'store/products/client';
import { IrelandImportReleasedNotification } from 'store/declarations/ireland/released-notification';
import { GoodsShipmentItem } from 'store/declarations/ireland/import-declaration';
import { CalculationSource, CalculationSummary } from './types';
import { calculateLocalData, calculateRevenueData } from './calculations';

export const useVatDutyCalculations = (
    declarationId: string | undefined,
    source: CalculationSource,
    initialFetch: boolean = true
) => {
    const [loading, setLoading] = useState(false);
    const [notificationResponse, setNotificationResponse] = useState<IrelandImportReleasedNotification | null>(null);
    const [productsResponse, setProductsResponse] = useState<GoodsShipmentItem[] | null>(null);

    const abortRef = useRef<AbortController | null>(null);

    // TODO: Currently, this work only for Ireland import released notifications and products.
    // Need to make this adaptable to any notification and products
    const fetch = useCallback(async () => {
        if (!declarationId) return;

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        try {
            const tasks: Promise<void>[] = [];

            if (source === 'revenue') {
                tasks.push(
                    getAisReleasedDeclarationNotification(declarationId, { signal: controller.signal }).then((res) => {
                        if (!controller.signal.aborted) setNotificationResponse(res);
                    })
                );
            }

            // Always get the local calculations even if we're in the revenue tab
            tasks.push(
                getAllIrelandImportDeclarationProducts(declarationId, { signal: controller.signal }).then((res) => {
                    if (!controller.signal.aborted) setProductsResponse(res);
                })
            );

            await Promise.all(tasks);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [declarationId, source]);

    const refetch = useCallback(async () => {
        setNotificationResponse(null);
        setProductsResponse(null);
        fetch();
    }, [fetch]);

    useEffect(() => {
        if (initialFetch) {
            fetch();
        }
    }, [initialFetch, fetch]);

    const localData: CalculationSummary = useMemo(() => calculateLocalData(productsResponse), [productsResponse]);

    const revenueData: CalculationSummary = useMemo(
        () => calculateRevenueData(notificationResponse),
        [notificationResponse]
    );

    return {
        loading,
        localData,
        revenueData,
        refetch,
    };
};
